import { ChatBotService } from '../services';

export interface StreamCallbacks {
  onToken: (token: string) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
  onNewMessage?: () => void;
  onComponent?: (messageId: string, component: { type: string; data: Record<string, any> }) => void;
  onContentUpdate?: (messageId: string, text: string) => void;
}

export interface StreamOptions {
  sessionId: string;
  message: string;
  assistantMessageId: string;
  userMessageId?: string;
}

/**
 * Handles streaming responses from the chatbot service
 */
export class StreamHandler {
  private service: ChatBotService;
  private currentStream: EventSource | null = null;
  private messageTexts = new Map<string, string>();

  constructor(service: ChatBotService) {
    this.service = service;
  }

  /**
   * Start streaming a message
   */
  stream(options: StreamOptions, callbacks: StreamCallbacks): void {
    this.stop();
    this.messageTexts.clear();

    this.currentStream = this.service.chatStream(
      options,
      // onToken
      (token: string, messageId?: string) => {
        if (messageId) {
          const current = this.messageTexts.get(messageId) || '';
          const updated = current + token;
          this.messageTexts.set(messageId, updated);
          if (callbacks.onContentUpdate) {
            callbacks.onContentUpdate(messageId, updated);
          }
        } else {
          // Fallback for tokens without messageId (backward compat)
          // Use the old approach - accumulate generically
          const fallbackKey = '__default__';
          const current = this.messageTexts.get(fallbackKey) || '';
          const updated = current + token;
          this.messageTexts.set(fallbackKey, updated);
          callbacks.onToken(updated);
        }
      },
      // onEnd
      () => {
        callbacks.onComplete();
        this.cleanup();
      },
      // onError
      (error: Error) => {
        callbacks.onError(error);
        this.cleanup();
      },
      // handlers
      {
        onMessageStart: () => {
          // Start a new message bubble after tool execution
          if (callbacks.onNewMessage) {
            callbacks.onNewMessage();
            this.messageTexts.clear();
          }
        },
        onComponent: (payload) => {
          if (callbacks.onComponent) {
            callbacks.onComponent(payload.messageId, { type: payload.type, data: payload.data });
          }
        },
      }
    );
  }

  /**
   * Stop current stream
   */
  stop(): void {
    if (this.currentStream) {
      this.currentStream.close();
      this.cleanup();
    }
  }

  private cleanup(): void {
    this.currentStream = null;
    this.messageTexts.clear();
  }

  /**
   * Check if currently streaming
   */
  isStreaming(): boolean {
    return this.currentStream !== null;
  }
}
